import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import { ItemsApi } from '@/lib/api/common/items';
import { FieldValuesApi } from '@/lib/api/item-store/field-values';
import type { Item, ItemCustomFieldValues, ItemPatchRequest } from '@/lib/api/types/items';

type CustomFieldEntry = { customFieldId: string; value: string | number | boolean | null };
type MultiSelectFieldEntry = {
  customFieldId: string;
  values: (string | number | boolean)[];
  previousEntryIds: number[];
};

const ITEMS_BULK_FETCH_KEY = ['itemsBulkFetch'];

export const useUpdateRoadmapItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      customFieldData,
      multiSelectFieldData,
      itemData,
    }: {
      itemId: number;
      customFieldData: CustomFieldEntry[] | null;
      multiSelectFieldData?: MultiSelectFieldEntry[] | null;
      itemData: Partial<ItemPatchRequest>;
    }) => {
      const itemUpdate = await ItemsApi.updateItem({
        itemId,
        data: itemData,
      });
      if (customFieldData) {
        for (const { customFieldId, value } of customFieldData) {
          await FieldValuesApi.upsert(itemId, Number(customFieldId), value);
        }
      }
      if (multiSelectFieldData) {
        for (const { customFieldId, values, previousEntryIds } of multiSelectFieldData) {
          const fieldId = Number(customFieldId);
          if (previousEntryIds.length > 0) {
            await FieldValuesApi.delete(itemId, fieldId, previousEntryIds);
          }
          if (values.length > 0) {
            await FieldValuesApi.upsertMany(itemId, fieldId, values);
          }
        }
      }
      return itemUpdate;
    },
    onMutate: async ({ itemId, itemData, customFieldData, multiSelectFieldData }) => {
      await queryClient.cancelQueries({ queryKey: ITEMS_BULK_FETCH_KEY });
      const prevEntries = queryClient.getQueriesData<Item[]>({ queryKey: ITEMS_BULK_FETCH_KEY });
      queryClient.setQueriesData<Item[]>(
        { queryKey: ITEMS_BULK_FETCH_KEY, exact: false },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return oldData.map((item) => {
            if (item.id === itemId) {
              const updatedCustomFields: Record<string, unknown> = { ...item.customFields };
              if (customFieldData) {
                for (const { customFieldId, value } of customFieldData) {
                  if (value === 'null' || value === null) {
                    delete updatedCustomFields[customFieldId];
                    continue;
                  }
                  updatedCustomFields[customFieldId] = value;
                }
              }
              if (multiSelectFieldData) {
                for (const { customFieldId, values } of multiSelectFieldData) {
                  if (values.length === 0) {
                    delete updatedCustomFields[customFieldId];
                  } else {
                    updatedCustomFields[customFieldId] = values;
                  }
                }
              }

              if (customFieldData || multiSelectFieldData) {
                return {
                  ...item,
                  customFields: updatedCustomFields as ItemCustomFieldValues,
                  ...(itemData as Partial<Item>),
                };
              }

              return {
                ...item,
                ...(itemData as Partial<Item>),
              };
            }
            return item;
          });
        },
      );
      return { prevEntries };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ITEMS_BULK_FETCH_KEY,
        refetchType: 'none',
      });
    },
    onError: (err, _newData, context) => {
      toast.error(err instanceof Error ? err.message : 'An error occurred while updating the item');
      for (const [key, data] of context?.prevEntries ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
  });
};
